import React from 'react';
import { BarChart3, Store } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CHART_GRADIENT_CSS, useChartCurrency } from './chartTheme';

interface StoreMetric {
  storeName: string;
  metric: number;
}

interface StoreComparisonBarChartProps {
  data: StoreMetric[];
  title?: string;
  metricLabel?: string;
  currencySymbol?: string;
  activeCurrencySymbol?: string;
}

export const StoreComparisonBarChart: React.FC<{ props: StoreComparisonBarChartProps; isStreaming?: boolean }> = ({
  props,
  isStreaming
}) => {
  const { t } = useTranslation();
  const {
    data = [],
    title = t('comparison.title'),
    metricLabel = t('common.total'),
  } = props || {};

  const activeCurrencySymbol = useChartCurrency(props);

  const validData = (Array.isArray(data) ? data : [])
    .filter(d => d && typeof d.metric === 'number' && Number.isFinite(d.metric))
    .sort((a, b) => b.metric - a.metric);

  if (validData.length === 0) {
    if (isStreaming) {
      return (
        <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-xl text-center animate-pulse">
           <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">{t('comparison.analyzing_metrics')}</p>
        </div>
      );
    }
    return (
      <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-xl text-center">
         <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('comparison.no_simulation_data')}</p>
      </div>
    );
  }

  const maxMetric = Math.max(...validData.map(d => d.metric));
  const total = validData.reduce((sum, d) => sum + d.metric, 0);
  const formatAmount = (value: number) => `${value < 0 ? '-' : ''}${activeCurrencySymbol}${Math.abs(value).toFixed(2)}`;

  return (
    <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="px-4 py-4 sm:px-5 bg-[#1a2b3c] flex justify-between items-center text-white">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 bg-emerald-500 rounded-xl shadow-lg shrink-0">
            <BarChart3 size={18} />
          </div>
          <div className="min-w-0">
            <h4 className="text-[10px] font-black uppercase tracking-widest text-emerald-400 leading-none mb-1">{t('comparison.market_analysis')}</h4>
            <p className="text-sm font-black text-white leading-tight">{title}</p>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-4 space-y-3 bg-white">
        <div role="region" aria-label={title} tabIndex={0} className="max-h-80 overflow-y-auto overscroll-contain space-y-4 pr-1 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
          {validData.map((d, i) => {
            // Guard against an all-zero/negative dataset (division by zero → NaN width).
            const percent = maxMetric > 0 ? Math.max(0, (d.metric / maxMetric) * 100) : 0;
            return (
              <div key={`${d.storeName}-${i}`} className="space-y-2 group">
                <div className="flex flex-wrap justify-between items-baseline gap-x-3 gap-y-1">
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <Store size={12} className="text-gray-300 shrink-0 mt-0.5" />
                    <span className="text-[11px] font-black text-[#1a2b3c] uppercase tracking-wide break-words" title={d.storeName}>{d.storeName}</span>
                  </div>
                  <span className="text-[11px] font-black text-emerald-600 shrink-0 tabular-nums">{formatAmount(d.metric)}</span>
                </div>
                <div className="w-full bg-gray-50 h-3 rounded-full overflow-hidden border border-gray-100 shadow-inner">
                  <div
                    className="h-full rounded-full transition-all duration-1000 ease-out shadow-sm"
                    style={{ width: `${percent}%`, backgroundImage: CHART_GRADIENT_CSS, backgroundSize: `${percent > 0 ? (100 / percent) * 100 : 100}% 100%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="pt-3 border-t border-gray-50 flex flex-wrap gap-2 justify-between items-center">
           <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{metricLabel}</p>
           <p className="text-lg font-black text-[#1a2b3c] tabular-nums">{formatAmount(total)}</p>
        </div>
      </div>
    </div>
  );
};
